import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Eye,
  Info,
  MapPin,
  FileText,
  Image as ImageIcon,
  Upload,
  X,
  Calendar,
  Settings,
  ExternalLink,
  Download,
  Loader
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import CustomSelect from '../components/common/CustomSelect';
import { 
  createTournament, 
  updateTournament, 
  getTournament 
} from '../services/tournamentService';
import { getAdminData } from '../services/adminAuth';

const AdminTournamentEditor = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const adminData = getAdminData();

  // Form state - added entry form fields
  const [formData, setFormData] = useState({
    tournament_name: '',
    tournament_short_name: '',
    tournament_year: new Date().getFullYear(),
    start_date: '',
    end_date: '',
    category: 'Mens_Open',
    format: '',
    rounds: 1,
    venue_club_id: '',
    venue_name: '',
    venue_address: '',
    venue_city: '',
    description: '',
    full_description: '',
    rules: '',
    special_notes: '',
    registration_link: '',
    entry_form_type: 'none',        // NEW: none, link, pdf
    entry_form_url: '',             // NEW: URL for link or PDF
    entry_form_pdf_filename: '',    // NEW: original PDF filename
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    poster_image_url: '',
    poster_storage_path: '',
    status: 'draft',
    is_featured: false,
    is_championship: false,
    is_qualifier: false
  });

  // UI state
  const [activeTab, setActiveTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [pdfUploadError, setPdfUploadError] = useState(null);
  const [clubs, setClubs] = useState([]);

  // Categories and options
  const categories = [
    { value: 'Mens_Open', label: "Men's Open" },
    { value: 'Mens_Mid_Am', label: "Men's Mid-Am" },
    { value: 'Mens_Senior', label: "Men's Senior" },
    { value: 'Mens_Super_Senior', label: "Men's Super Senior" },
    { value: 'Ladies_Open', label: "Ladies Open" },
    { value: 'Ladies_Mid_Am', label: "Ladies Mid-Am" },
    { value: 'Ladies_Senior', label: "Ladies Senior" },
    { value: 'Ladies_Super_Senior', label: "Ladies Super Senior" },
    { value: 'Junior_Boys', label: "Junior Boys" },
    { value: 'Junior_Girls', label: "Junior Girls" },
    { value: 'Mixed', label: "Mixed" },
    { value: 'Country_Districts', label: "Country Districts" }
  ];

  const formats = [
    'Stroke Play',
    'Match Play',
    'Stableford',
    '4-Ball',
    'Foursomes',
    'Texas Scramble',
    'Better Ball'
  ];

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  // Load tournament data if editing
  useEffect(() => {
    if (isEditMode) {
      fetchTournamentData();
    }
    fetchClubs();
  }, [id]);

  const fetchTournamentData = async () => {
    try {
      setLoading(true);
      const response = await getTournament(id);
      
      const tournamentData = response.data;
      setFormData({
        tournament_name: tournamentData.tournament_name || '',
        tournament_short_name: tournamentData.tournament_short_name || '',
        tournament_year: tournamentData.tournament_year || new Date().getFullYear(),
        start_date: tournamentData.start_date || '',
        end_date: tournamentData.end_date || '',
        category: tournamentData.category || 'Mens_Open',
        format: tournamentData.format || '',
        rounds: tournamentData.rounds || 1,
        venue_club_id: tournamentData.venue_club_id || '',
        venue_name: tournamentData.venue_name || '',
        venue_address: tournamentData.venue_address || '',
        venue_city: tournamentData.venue_city || '',
        description: tournamentData.description || '',
        full_description: tournamentData.full_description || '',
        rules: tournamentData.rules || '',
        special_notes: tournamentData.special_notes || '',
        registration_link: tournamentData.registration_link || '',
        entry_form_type: tournamentData.entry_form_type || 'none',
        entry_form_url: tournamentData.entry_form_url || '',
        entry_form_pdf_filename: tournamentData.entry_form_pdf_filename || '',
        contact_person: tournamentData.contact_person || '',
        contact_email: tournamentData.contact_email || '',
        contact_phone: tournamentData.contact_phone || '',
        poster_image_url: tournamentData.poster_image_url || '',
        poster_storage_path: tournamentData.poster_storage_path || '',
        status: tournamentData.status || 'draft',
        is_featured: tournamentData.is_featured || false,
        is_championship: tournamentData.is_championship || false,
        is_qualifier: tournamentData.is_qualifier || false
      });
    } catch (error) {
      console.error('Error fetching tournament:', error);
      alert('Failed to load tournament data');
    } finally {
      setLoading(false);
    }
  };

  const fetchClubs = async () => {
    try {
      const response = await fetch('https://api.fsgu.co.za/get-clubs.php');
      const data = await response.json();
      if (data.success) {
        setClubs(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching clubs:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleClubSelect = (e) => {
    const clubId = e.target.value;
    const selectedClub = clubs.find(c => c.id === parseInt(clubId));
    
    if (selectedClub) {
      setFormData(prev => ({
        ...prev,
        venue_club_id: clubId,
        venue_name: selectedClub.name,
        venue_address: selectedClub.address || '',
        venue_city: selectedClub.city || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        venue_club_id: '',
        venue_name: '',
        venue_address: '',
        venue_city: ''
      }));
    }
  };

  // Entry form type change handler
  const handleEntryFormTypeChange = (type) => {
    setPdfUploadError(null);
    
    if (type === 'none') {
      setFormData(prev => ({
        ...prev,
        entry_form_type: 'none',
        entry_form_url: '',
        entry_form_pdf_filename: '',
        registration_link: ''
      }));
    } else if (type === 'link') {
      setFormData(prev => ({
        ...prev,
        entry_form_type: 'link',
        entry_form_pdf_filename: ''
      }));
    } else if (type === 'pdf') {
      setFormData(prev => ({
        ...prev,
        entry_form_type: 'pdf',
        registration_link: ''
      }));
    }
  };

  // Handle link input change
  const handleLinkChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      registration_link: value,
      entry_form_url: value
    }));
  };

  // Handle PDF upload
  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setPdfUploadError('Please select a PDF file');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setPdfUploadError('File size must be less than 20MB');
      return;
    }

    try {
      setUploadingPdf(true);
      setPdfUploadError(null);

      const uploadData = new FormData();
      uploadData.append('pdf', file);

      const response = await fetch('https://api.fsgu.co.za/upload-pdf.php', {
        method: 'POST',
        body: uploadData
      });

      const data = await response.json();

      if (data.success) {
        setFormData(prev => ({
          ...prev,
          entry_form_url: data.url,
          entry_form_type: 'pdf',
          entry_form_pdf_filename: data.original_name || file.name,
          registration_link: ''
        }));
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('PDF upload error:', error);
      setPdfUploadError(error.message || 'Failed to upload PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  // Remove uploaded PDF
  const handleRemovePdf = () => {
    setFormData(prev => ({
      ...prev,
      entry_form_url: '',
      entry_form_type: 'none',
      entry_form_pdf_filename: ''
    }));
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    try {
      setUploadingImage(true);
      
      const compressedBase64 = await compressImage(file, 0.8);
      
      setFormData(prev => ({
        ...prev,
        [`${type}_image_url`]: compressedBase64,
        [`${type}_storage_path`]: file.name
      }));
      
      alert('Image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const compressImage = (file, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          const maxWidth = 1920;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedBase64);
        };
        
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (type) => {
    if (!window.confirm('Remove this image?')) return;
    
    setFormData(prev => ({
      ...prev,
      [`${type}_image_url`]: '',
      [`${type}_storage_path`]: ''
    }));
  };

  const handleSave = async (publishStatus = formData.status) => {
    if (!formData.tournament_name || !formData.start_date || !formData.end_date || !formData.venue_name) {
      alert('Please fill in all required fields (Tournament Name, Dates, Venue)');
      return;
    }

    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      alert('End date must be after start date');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        ...formData,
        status: publishStatus,
        venue_club_id: formData.venue_club_id || null
      };

      if (isEditMode) {
        await updateTournament(id, payload, adminData.id);
        alert('Tournament updated successfully!');
      } else {
        await createTournament(payload, adminData.id);
        alert('Tournament created successfully!');
      }
      
      navigate('/admin/tournaments');
    } catch (error) {
      console.error('Error saving tournament:', error);
      alert(error.message || 'Failed to save tournament. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = () => {
    handleSave('published');
  };

  const handleSaveDraft = () => {
    handleSave('draft');
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
      }`}>
        <div className="text-center">
          <div className={`inline-block animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent ${
            isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
          }`}></div>
          <p className={`mt-4 ${
            isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
          }`}>
            Loading tournament...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${
      isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
    }`}>
      {/* Header */}
      <div className={`sticky top-0 z-30 border-b ${
        isDarkMode
          ? 'bg-fsgu-gray-900/95 border-fsgu-gray-700 backdrop-blur-sm'
          : 'bg-fsgu-silver-50/95 border-fsgu-silver-200 backdrop-blur-sm'
      }`}>
        <div className="container-custom py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Left: Back + Title */}
            <div className="flex items-center gap-3">
              <Link
                to="/admin/tournaments"
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? 'hover:bg-fsgu-gray-800 text-fsgu-silver-300'
                    : 'hover:bg-fsgu-silver-100 text-fsgu-silver-600'
                }`}
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className={`text-3xl sm:text-4xl font-bold font-cursive ${
                  isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                }`}>
                  {isEditMode ? 'Edit Tournament' : 'New Tournament'}
                </h1>
                <p className={`text-sm ${
                  isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                }`}>
                  {isEditMode ? formData.tournament_name : 'Create a new tournament event'}
                </p>
              </div>
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveDraft}
                className="btn btn-outline inline-flex items-center"
                disabled={saving}
              >
                <Save className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Save Draft</span>
                <span className="sm:hidden">Draft</span>
              </button>
              
              <button
                onClick={handlePublish}
                className="btn btn-primary inline-flex items-center"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    {formData.status === 'published' ? 'Update' : 'Publish'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="container-custom pt-6">
        <div className={`border-b ${
          isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
        }`}>
          <div className="flex space-x-1 overflow-x-auto pb-px">
            {[
              { id: 'basic', label: 'Basic Info', icon: Info },
              { id: 'schedule', label: 'Schedule', icon: Calendar },
              { id: 'venue', label: 'Venue', icon: MapPin },
              { id: 'details', label: 'Details', icon: FileText },
              { id: 'media', label: 'Media', icon: ImageIcon },
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? isDarkMode
                        ? 'text-fsgu-green-400 border-b-2 border-fsgu-green-400'
                        : 'text-fsgu-green-500 border-b-2 border-fsgu-green-500'
                      : isDarkMode
                      ? 'text-fsgu-silver-400 hover:text-fsgu-silver-300'
                      : 'text-fsgu-silver-600 hover:text-fsgu-silver-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container-custom py-6 pb-12">
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <div className="max-w-4xl mx-auto space-y-6">
            <div className={`p-6 rounded-xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <h3 className={`text-lg font-semibold mb-6 ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                Tournament Information
              </h3>

              <div className="space-y-4">
                {/* Tournament Name */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    Tournament Name <span className="text-fsgu-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="tournament_name"
                    value={formData.tournament_name}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="e.g., Free State Open Championship 2025"
                    required
                  />
                </div>

                {/* Short Name and Year */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                    }`}>
                      Short Name
                    </label>
                    <input
                      type="text"
                      name="tournament_short_name"
                      value={formData.tournament_short_name}
                      onChange={handleInputChange}
                      className="input"
                      placeholder="e.g., FS Open 2025"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                    }`}>
                      Year <span className="text-fsgu-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="tournament_year"
                      value={formData.tournament_year}
                      onChange={handleInputChange}
                      className="input"
                      required
                    />
                  </div>
                </div>

                {/* Category, Format, Rounds */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                    }`}>
                      Category <span className="text-fsgu-red-500">*</span>
                    </label>
                    <CustomSelect
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      options={categories}
                      placeholder="Select category"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                    }`}>
                      Format
                    </label>
                    <CustomSelect
                      name="format"
                      value={formData.format}
                      onChange={handleInputChange}
                      options={[
                        { value: '', label: 'Select format' },
                        ...formats.map(f => ({ value: f, label: f }))
                      ]}
                      placeholder="Select format"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                    }`}>
                      Rounds
                    </label>
                    <input
                      type="number"
                      name="rounds"
                      value={formData.rounds}
                      onChange={handleInputChange}
                      className="input"
                      min="1"
                    />
                  </div>
                </div>

                {/* Short Description */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    Short Description
                    <span className={`text-xs ml-2 ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                    }`}>
                      (max 500 characters)
                    </span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="input"
                    rows="3"
                    maxLength="500"
                    placeholder="Brief description for tournament cards and previews..."
                  />
                  <div className={`text-xs mt-1 text-right ${
                    isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                  }`}>
                    {(formData.description || '').length} / 500
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <div className="max-w-4xl mx-auto space-y-6">
            <div className={`p-6 rounded-xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <h3 className={`text-lg font-semibold mb-6 ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                Tournament Dates
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    Start Date <span className="text-fsgu-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    className="input"
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    End Date <span className="text-fsgu-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    className="input"
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Venue Tab */}
        {activeTab === 'venue' && (
          <div className="max-w-4xl mx-auto space-y-6">
            <div className={`p-6 rounded-xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <h3 className={`text-lg font-semibold mb-6 ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                Venue Information
              </h3>

              <div className="space-y-4">
                {/* Club Dropdown */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    Select Golf Club
                  </label>
                  <CustomSelect
                    name="venue_club_id"
                    value={formData.venue_club_id}
                    onChange={handleClubSelect}
                    options={[
                      { value: '', label: 'Select a club (or enter manually below)' },
                      ...clubs.map(club => ({
                        value: club.id.toString(),
                        label: `${club.name} - ${club.city}`
                      }))
                    ]}
                    placeholder="Select a club"
                  />
                </div>

                {/* Manual Venue Entry */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    Venue Name <span className="text-fsgu-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="venue_name"
                    value={formData.venue_name}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Golf Club Name"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                    }`}>
                      Address
                    </label>
                    <input
                      type="text"
                      name="venue_address"
                      value={formData.venue_address}
                      onChange={handleInputChange}
                      className="input"
                      placeholder="Street address"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                    }`}>
                      City
                    </label>
                    <input
                      type="text"
                      name="venue_city"
                      value={formData.venue_city}
                      onChange={handleInputChange}
                      className="input"
                      placeholder="City"
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    Special Notes
                  </label>
                  <textarea
                    name="special_notes"
                    value={formData.special_notes}
                    onChange={handleInputChange}
                    className="input"
                    rows="3"
                    placeholder="Parking, dress code, etc..."
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Details Tab - Updated with Entry Form Section */}
        {activeTab === 'details' && (
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Entry Form / Registration Section - UPDATED */}
            <div className={`p-6 rounded-xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <h3 className={`text-lg font-semibold mb-2 flex items-center ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                <FileText className="w-5 h-5 mr-2 text-fsgu-green-500" />
                Entry Form / Registration
              </h3>
              <p className={`text-sm mb-4 ${
                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
              }`}>
                Provide either a Golf Genius registration link OR upload an entry form PDF
              </p>

              {/* Type Selection Tabs */}
              <div className={`flex rounded-lg p-1 mb-4 ${
                isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-100'
              }`}>
                <button
                  type="button"
                  onClick={() => handleEntryFormTypeChange('none')}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    formData.entry_form_type === 'none'
                      ? isDarkMode
                        ? 'bg-fsgu-gray-700 text-fsgu-silver-100 shadow'
                        : 'bg-white text-fsgu-silver-900 shadow'
                      : isDarkMode
                      ? 'text-fsgu-silver-400 hover:text-fsgu-silver-200'
                      : 'text-fsgu-silver-600 hover:text-fsgu-silver-900'
                  }`}
                >
                  None
                </button>
                <button
                  type="button"
                  onClick={() => handleEntryFormTypeChange('link')}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center ${
                    formData.entry_form_type === 'link'
                      ? isDarkMode
                        ? 'bg-fsgu-gray-700 text-fsgu-silver-100 shadow'
                        : 'bg-white text-fsgu-silver-900 shadow'
                      : isDarkMode
                      ? 'text-fsgu-silver-400 hover:text-fsgu-silver-200'
                      : 'text-fsgu-silver-600 hover:text-fsgu-silver-900'
                  }`}
                >
                  <ExternalLink className="w-4 h-4 mr-1.5" />
                  Link
                </button>
                <button
                  type="button"
                  onClick={() => handleEntryFormTypeChange('pdf')}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center ${
                    formData.entry_form_type === 'pdf'
                      ? isDarkMode
                        ? 'bg-fsgu-gray-700 text-fsgu-silver-100 shadow'
                        : 'bg-white text-fsgu-silver-900 shadow'
                      : isDarkMode
                      ? 'text-fsgu-silver-400 hover:text-fsgu-silver-200'
                      : 'text-fsgu-silver-600 hover:text-fsgu-silver-900'
                  }`}
                >
                  <Upload className="w-4 h-4 mr-1.5" />
                  PDF
                </button>
              </div>

              {/* Link Input */}
              {formData.entry_form_type === 'link' && (
                <div className="space-y-3">
                  <input
                    type="url"
                    value={formData.registration_link || formData.entry_form_url || ''}
                    onChange={handleLinkChange}
                    className="input"
                    placeholder="https://www.golfgenius.com/..."
                  />
                  
                  {(formData.registration_link || formData.entry_form_url) && (
                    <a
                      href={formData.registration_link || formData.entry_form_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center text-sm ${
                        isDarkMode 
                          ? 'text-fsgu-green-400 hover:text-fsgu-green-300' 
                          : 'text-fsgu-green-600 hover:text-fsgu-green-700'
                      }`}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Test link
                    </a>
                  )}
                </div>
              )}

              {/* PDF Upload */}
              {formData.entry_form_type === 'pdf' && (
                <div className="space-y-3">
                  {!(formData.entry_form_url && formData.entry_form_type === 'pdf') ? (
                    <>
                      <div className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                        isDarkMode 
                          ? 'border-fsgu-gray-600 hover:border-fsgu-gray-500' 
                          : 'border-fsgu-silver-300 hover:border-fsgu-silver-400'
                      }`}>
                        <input
                          type="file"
                          accept=".pdf,application/pdf"
                          onChange={handlePdfUpload}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          disabled={uploadingPdf}
                        />
                        
                        {uploadingPdf ? (
                          <div className="flex flex-col items-center">
                            <Loader className={`w-8 h-8 animate-spin mb-2 ${
                              isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                            }`} />
                            <p className={isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'}>
                              Uploading...
                            </p>
                          </div>
                        ) : (
                          <>
                            <Upload className={`w-8 h-8 mx-auto mb-2 ${
                              isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                            }`} />
                            <p className={`font-medium ${
                              isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                            }`}>
                              Click to upload PDF
                            </p>
                            <p className={`text-sm mt-1 ${
                              isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                            }`}>
                              or drag and drop (max 20MB)
                            </p>
                          </>
                        )}
                      </div>
                      
                      {pdfUploadError && (
                        <p className="text-sm text-red-500">{pdfUploadError}</p>
                      )}
                    </>
                  ) : (
                    <div className={`flex items-center justify-between p-4 rounded-lg ${
                      isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
                    }`}>
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${
                          isDarkMode ? 'bg-fsgu-red-500/20' : 'bg-fsgu-red-100'
                        }`}>
                          <FileText className={`w-5 h-5 ${
                            isDarkMode ? 'text-fsgu-red-400' : 'text-fsgu-red-600'
                          }`} />
                        </div>
                        <div>
                          <p className={`font-medium text-sm ${
                            isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-800'
                          }`}>
                            {formData.entry_form_pdf_filename || 'Entry Form.pdf'}
                          </p>
                          <p className={`text-xs ${
                            isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                          }`}>
                            PDF uploaded successfully
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <a
                          href={formData.entry_form_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`p-2 rounded-lg transition-colors ${
                            isDarkMode 
                              ? 'hover:bg-fsgu-gray-700 text-fsgu-silver-400' 
                              : 'hover:bg-fsgu-silver-200 text-fsgu-silver-600'
                          }`}
                          title="View PDF"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                        <button
                          type="button"
                          onClick={handleRemovePdf}
                          className={`p-2 rounded-lg transition-colors ${
                            isDarkMode 
                              ? 'hover:bg-fsgu-red-500/20 text-fsgu-red-400' 
                              : 'hover:bg-fsgu-red-100 text-fsgu-red-600'
                          }`}
                          title="Remove PDF"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* None selected info */}
              {formData.entry_form_type === 'none' && (
                <p className={`text-sm italic ${
                  isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                }`}>
                  No entry form will be shown to users. You can add one later.
                </p>
              )}
            </div>

            <div className={`p-6 rounded-xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <h3 className={`text-lg font-semibold mb-6 ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                Full Description
              </h3>
              <textarea
                name="full_description"
                value={formData.full_description}
                onChange={handleInputChange}
                className="input"
                rows="8"
                placeholder="Detailed tournament description, history, format details, etc..."
              />
            </div>

            <div className={`p-6 rounded-xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <h3 className={`text-lg font-semibold mb-6 ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                Tournament Rules
              </h3>
              <textarea
                name="rules"
                value={formData.rules}
                onChange={handleInputChange}
                className="input"
                rows="8"
                placeholder="Tournament-specific rules, local rules, etc..."
              />
            </div>
          </div>
        )}

        {/* Media Tab */}
        {activeTab === 'media' && (
          <div className="max-w-2xl mx-auto">
            {/* Poster Image Only */}
            <div className={`p-6 rounded-xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <h3 className={`text-lg font-semibold mb-4 ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                Tournament Poster
              </h3>

              {formData.poster_image_url ? (
                <div className="relative">
                  <img
                    src={formData.poster_image_url}
                    alt="Tournament poster"
                    className="w-full h-96 object-cover rounded-lg"
                  />
                  <button
                    onClick={() => handleRemoveImage('poster')}
                    className={`absolute top-2 right-2 p-2 rounded-lg transition-colors ${
                      isDarkMode
                        ? 'bg-fsgu-gray-900/80 text-fsgu-red-400 hover:bg-fsgu-red-900/50'
                        : 'bg-white/80 text-fsgu-red-600 hover:bg-fsgu-red-50'
                    }`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className={`flex flex-col items-center justify-center h-96 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  isDarkMode
                    ? 'border-fsgu-gray-700 hover:border-fsgu-green-500 bg-fsgu-gray-900/50'
                    : 'border-fsgu-silver-300 hover:border-fsgu-green-500 bg-fsgu-silver-50'
                }`}>
                  <Upload className={`w-16 h-16 mb-4 ${
                    isDarkMode ? 'text-fsgu-silver-600' : 'text-fsgu-silver-400'
                  }`} />
                  <p className={`text-base font-medium mb-1 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    Click to upload tournament poster
                  </p>
                  <p className={`text-sm ${
                    isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                  }`}>
                    PNG, JPG up to 5MB
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'poster')}
                    className="hidden"
                    disabled={uploadingImage}
                  />
                </label>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="max-w-4xl mx-auto space-y-6">
            <div className={`p-6 rounded-xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <h3 className={`text-lg font-semibold mb-6 ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                Status & Visibility
              </h3>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    Status
                  </label>
                  <CustomSelect
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    options={statusOptions}
                    placeholder="Select status"
                  />
                </div>

                <div className="space-y-3">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_featured"
                      checked={formData.is_featured}
                      onChange={handleInputChange}
                      className="rounded border-gray-300"
                    />
                    <span className={`text-sm ${
                      isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                    }`}>
                      Featured Tournament
                    </span>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_championship"
                      checked={formData.is_championship}
                      onChange={handleInputChange}
                      className="rounded border-gray-300"
                    />
                    <span className={`text-sm ${
                      isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                    }`}>
                      Championship Event
                    </span>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_qualifier"
                      checked={formData.is_qualifier}
                      onChange={handleInputChange}
                      className="rounded border-gray-300"
                    />
                    <span className={`text-sm ${
                      isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                    }`}>
                      Qualifier Event
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className={`p-6 rounded-xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <h3 className={`text-lg font-semibold mb-6 ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                Contact Information
              </h3>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    Contact Person
                  </label>
                  <input
                    type="text"
                    name="contact_person"
                    value={formData.contact_person}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Tournament Director"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    Email
                  </label>
                  <input
                    type="email"
                    name="contact_email"
                    value={formData.contact_email}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="operations@fsgu.co.za"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="051-123-4567"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTournamentEditor;